import React, { useCallback, useEffect, useState } from "react";
import { PageHeader, Card, Loading, ErrorState, Segmented } from "../components/ui";
import ImportPanel from "../components/ImportPanel";
import DepartmentMap from "../components/DepartmentMap";
import AccountHeads from "../components/AccountHeads";
import SalaryMap from "../components/SalaryMap";
import EmployeeManagers from "../components/EmployeeManagers";
import DropStatus from "../components/DropStatus";
import PeriodLocks from "../components/PeriodLocks";
import ManualEntries from "../components/ManualEntries";
import Prepaid from "./Prepaid";
import Timesheets from "./Timesheets";
import {
  listUsers, resetPassword, setPages, createUser, migrateAdmin, listImports,
} from "../api";

const ALL_PAGES = [
  { key: "labour",      label: "Dashboard" },
  { key: "equity",      label: "Equity" },
  { key: "projects",    label: "Project" },
  { key: "pl",          label: "P&L" },
  { key: "expenses",    label: "Expenses" },
  { key: "receivables", label: "Receivables" },
  { key: "unbilled",    label: "Unbilled" },
];

export default function Admin({ username }) {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [section, setSection]   = useState("users");
  const [imports, setImports]   = useState([]);

  // password reset state
  const [pwUser, setPwUser]     = useState(null);
  const [newPw, setNewPw]       = useState("");
  const [pwMsg, setPwMsg]       = useState("");
  const [pwErr, setPwErr]       = useState("");

  // create user state
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser]       = useState("");
  const [newUserPw, setNewUserPw]   = useState("");
  const [createMsg, setCreateMsg]   = useState("");
  const [createErr, setCreateErr]   = useState("");

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listUsers();
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // auto-migrate on first load
  useEffect(() => {
    (async () => {
      try {
        await migrateAdmin();
      } catch (_) {
        // column may already exist — that's fine
      }
      try {
        setImports(await listImports());
      } catch (_) {
        // older backend without the imports registry
      }
      loadUsers();
    })();
  }, [loadUsers]);

  const handleResetPw = async (u) => {
    setPwMsg(""); setPwErr("");
    if (!newPw || newPw.length < 8 || !/[!@#$%^&*(),.?":{}|<>]/.test(newPw)) {
      setPwErr("Must be 8+ chars with a special character.");
      return;
    }
    try {
      await resetPassword(u.id, newPw);
      setPwMsg(`Password reset for ${u.username.toUpperCase()}`);
      setNewPw("");
      setPwUser(null);
    } catch (err) {
      setPwErr(err.message);
    }
  };

  const handleSetPages = async (u, pages) => {
    try {
      const result = await setPages(u.id, pages);
      setUsers((prev) =>
        prev.map((x) => (x.id === u.id ? { ...x, pages: result.user.pages } : x))
      );
    } catch (err) {
      alert(err.message);
    }
  };

  const handleCreate = async () => {
    setCreateMsg(""); setCreateErr("");
    if (!newUser || !/^[a-z]{2,10}$/.test(newUser)) {
      setCreateErr("Username: 2-10 lowercase letters.");
      return;
    }
    if (!newUserPw || newUserPw.length < 8 || !/[!@#$%^&*(),.?":{}|<>]/.test(newUserPw)) {
      setCreateErr("Password must be 8+ chars with a special character.");
      return;
    }
    try {
      await createUser(newUser, newUserPw);
      setCreateMsg(`User ${newUser} created`);
      setNewUser(""); setNewUserPw(""); setShowCreate(false);
      loadUsers();
    } catch (err) {
      setCreateErr(err.message);
    }
  };

  if (loading) return <Loading label="Loading users…" />;
  if (error)   return <ErrorState message={error} onRetry={loadUsers} />;

  return (
    <div className="page">
      <PageHeader title="Admin" subtitle="Manage users, page access and data imports">
        <Segmented
          value={section}
          onChange={setSection}
          options={[
            { value: "users", label: "Users" },
            { value: "departments", label: "Departments" },
            { value: "accounts", label: "Accounts" },
            { value: "salary", label: "Grouping map" },
            { value: "empmgr", label: "Employee managers" },
            { value: "journal", label: "Manual entries" },
            { value: "prepaid", label: "Prepaid" },
            { value: "timesheets", label: "Timesheets" },
            { value: "imports", label: `Imports${imports.length ? ` (${imports.length})` : ""}` },
          ]}
        />
        {section === "users" && (!showCreate ? (
          <button className="btn btn--primary" onClick={() => setShowCreate(true)}>
            New user
          </button>
        ) : (
          <button className="btn" onClick={() => { setShowCreate(false); setCreateMsg(""); setCreateErr(""); }}>
            Cancel
          </button>
        ))}
      </PageHeader>

      {section === "departments" && <DepartmentMap />}
      {section === "empmgr" && <EmployeeManagers />}

      {section === "accounts" && <AccountHeads />}

      {section === "salary" && <SalaryMap />}

      {section === "journal" && <ManualEntries />}

      {section === "prepaid" && <Prepaid embedded />}

      {section === "timesheets" && <Timesheets username={username} embedded />}

      {section === "imports" && (
        <div className="stack">
          <DropStatus />
          <PeriodLocks />
          {imports.length === 0 ? (
            <Card title="Imports">
              <p className="muted">No imports are configured on the backend.</p>
            </Card>
          ) : (
            imports.map((imp) => (
              <ImportPanel
                key={imp.key}
                importKey={imp.key}
                label={imp.label}
                table={imp.table}
                mode={imp.mode}
                scope={imp.scope}
              />
            ))
          )}
        </div>
      )}

      {section === "users" && showCreate && (
        <Card title="Create user" className="admin-create">
          <div className="admin-form-row">
            <input
              className="input"
              placeholder="username (lowercase)"
              value={newUser}
              onChange={(e) => setNewUser(e.target.value.toLowerCase())}
            />
            <input
              className="input"
              type="password"
              placeholder="password"
              value={newUserPw}
              onChange={(e) => setNewUserPw(e.target.value)}
            />
            <button className="btn btn--primary" onClick={handleCreate}>Create</button>
          </div>
          {createMsg && <div className="admin-ok">{createMsg}</div>}
          {createErr && <div className="admin-err">{createErr}</div>}
        </Card>
      )}

      {section === "users" && (
      <Card title="Users" note={`${users.length} total`}>
        <div className="tbl-wrap">
          <table className="tbl tbl-zebra">
            <thead>
              <tr>
                <th>User</th>
                <th>Pages</th>
                <th style={{ width: 1 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isAdmin_ = u.username.toUpperCase() === "FD";
                const pages = u.pages;
                return (
                  <tr key={u.id}>
                    <td>
                      <span className="admin-username">{u.username.toUpperCase()}</span>
                      {u.locked_until && new Date(u.locked_until) > new Date() && (
                        <span className="badge badge--neg" style={{ marginLeft: 6 }}>Locked</span>
                      )}
                    </td>
                    <td>
                      <div className="admin-pages">
                        {isAdmin_ ? (
                          <span className="badge badge--accent">All pages (admin)</span>
                        ) : (
                          ALL_PAGES.map((p) => {
                            const allowed = !pages || pages.includes(p.key);
                            return (
                              <button
                                key={p.key}
                                className={`admin-page-chip${allowed ? " is-on" : ""}`}
                                onClick={() => {
                                  if (pages) {
                                    // has a restriction list
                                    if (allowed) {
                                      // remove from list
                                      const next = pages.filter((k) => k !== p.key);
                                      handleSetPages(u, next.length > 0 ? next : []);
                                    } else {
                                      handleSetPages(u, [...pages, p.key]);
                                    }
                                  } else {
                                    // no restrictions yet — create list with all except this one
                                    handleSetPages(u, ALL_PAGES.map((x) => x.key).filter((k) => k !== p.key));
                                  }
                                }}
                                title={allowed ? `Revoke ${p.label}` : `Grant ${p.label}`}
                              >
                                {p.label}
                              </button>
                            );
                          })
                        )}
                      </div>
                      {!isAdmin_ && pages && pages.length === 0 && (
                        <div className="admin-note">No pages — user sees nothing</div>
                      )}
                      {!isAdmin_ && !pages && (
                        <div className="admin-note">All pages (no restrictions)</div>
                      )}
                    </td>
                    <td className="admin-actions">
                      {pwUser?.id === u.id ? (
                        <div className="admin-pw-reset">
                          <input
                            className="input"
                            type="password"
                            placeholder="new password"
                            value={newPw}
                            onChange={(e) => setNewPw(e.target.value)}
                            autoFocus
                            onKeyDown={(e) => e.key === "Enter" && handleResetPw(u)}
                          />
                          <button className="btn btn--primary btn--sm" onClick={() => handleResetPw(u)}>
                            Save
                          </button>
                          <button className="btn btn--sm" onClick={() => { setPwUser(null); setNewPw(""); setPwMsg(""); setPwErr(""); }}>
                            Cancel
                          </button>
                          {pwMsg && <div className="admin-ok">{pwMsg}</div>}
                          {pwErr && <div className="admin-err">{pwErr}</div>}
                        </div>
                      ) : (
                        <button
                          className="btn btn--sm"
                          onClick={() => { setPwUser(u); setNewPw(""); setPwMsg(""); setPwErr(""); }}
                        >
                          Reset password
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      )}
    </div>
  );
}
